import { skipToken } from '@apollo/client/react'
import { useUncontrolled } from '@mantine/hooks'
import { Button, Tab, Tabs } from '@mui/material'
import { omit, pickBy } from 'lodash-es'
import type { FC, ReactNode } from 'react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import FluentCheckmark16Filled from '~icons/fluent/checkmark-16-filled'
import FluentEyeHide20Filled from '~icons/fluent/eye-hide-20-filled'
import IcOutlineSave from '~icons/ic/outline-save'
import MaterialSymbolsContentCopyOutline from '~icons/material-symbols/content-copy-outline'
import MediaPreview from 'src/components/Common/MediaPreview'
import { UpsertNoteDialog } from 'src/components/GenerationTask/MultiColumnGenerationForm/Generation/RightCol/TaskNotes'
import { INPAINT_MODELS } from 'src/components/GenerationTask/MultiColumnGenerationForm/GenerativeFill/GenerativeFillEditorV2/constants'
import { StyleSeedLabel } from 'src/components/GenerationTask/StyleSeedDisplay'
import { LikeHeart } from 'src/components/LikeButton/LikeHeart'
import { I2vTaskFrames } from 'src/components/TaskList/I2vTaskFrames'
import Text from 'src/components/ui/Text'
import UserAvatar from 'src/components/UserAvatar'
import { useImageArtFilters } from 'src/features/ImageArtFilters/useImageArtFilters'
import { useCopy } from 'src/hooks/useCopy'
import { useMyInfo } from 'src/services/apollo/auth'
import type {
  ArtworkDetailFragment,
  ArtworkWithTaskDetailBaseFragment,
  DetailedTaskParameters,
} from 'src/services/apollo/generated/graphql'
import {
  ArtworkVisibilityType,
  RefType,
} from 'src/services/apollo/generated/graphql'
import { useGetWorkflowForMarketByWorkflowId } from 'src/services/apollo/workflow'
import { getArtworkParameters } from 'src/services/helpers/artwork'
import { useLike } from 'src/services/helpers/like'
import { useMe } from 'src/services/helpers/user'
import { useInferenceProfileData } from 'src/services/orpc/inferenceProfile'
import { useModelVersion } from 'src/stores/model'
import { cx } from 'src/utils/cx'
import {
  formatQualityTagForDisplay,
  isMMDIT26AModel,
  normalizeModelType,
} from 'src/utils/model'
import { getI18nKeyForI2vModelName } from 'src/utils/task'
import { track } from 'src/utils/tracker'
import { getUserDisplayName } from 'src/utils/user'
import { openSignModal } from '../../Account/utils'
import { ArtworkPromptsWithHidden } from './ArtworkPrompts'
import { ArtworkTaskCapabilityItems } from './ArtworkTaskCapabilities'
import type { MentionMediaMap } from './mentionMedia'
import type { ParsedParamsForArtworkDetail } from './parser'
import { parseDisplayableParametersFromTask } from './parser'

type ArtworkParametersProps = {
  artwork: ArtworkDetailFragment
  artworkLoras: ArtworkWithTaskDetailBaseFragment['loras']
  taskParameters?: DetailedTaskParameters
}

const TabChoices = {
  prompts: 'prompts',
} as const

type TabChoice = keyof typeof TabChoices

export function ArtworkParameterInfo(props: ArtworkParametersProps) {
  const { artwork, artworkLoras, taskParameters } = props
  const parsedParams = parseDisplayableParametersFromTask(
    taskParameters,
    artwork,
  )

  if (parsedParams?.type === 'workflow')
    return <ArtworkParametersForWorkflow parsedParams={parsedParams} />
  if (parsedParams?.type === 'art-filter')
    return <ArtworkParametersForArtFilter parsedParams={parsedParams} />
  if (!parsedParams?.type) return null
  return (
    <ArtworkParameterInfoGenerator
      parsedParams={parsedParams}
      // For Note Data and Animate This Image only
      // Don't use these raw values for display
      artwork={artwork}
      artworkLoras={artworkLoras}
      taskParameters={taskParameters}
    />
  )
}

export function ArtworkParameterInfoGenerator(
  props: {
    parsedParams: Exclude<
      ParsedParamsForArtworkDetail,
      { type: 'workflow' } | { type: 'art-filter' }
    >
  } & ArtworkParametersProps,
) {
  const { t } = useTranslation(['artwork', 'generation', 'action'])
  const [copy, copied] = useCopy()

  const { parsedParams } = props

  const { data: me } = useMyInfo()
  const isOwnArtwork = me?.id === parsedParams?.authorId

  const [openSaveDialog, setOpenSaveDialog] = useState(false)

  const prefilledNoteData = useMemo(
    () => ({
      ...getArtworkParameters(props.artwork, props.taskParameters),
      title: props.artwork.title,
      baseImageMedia: props.artwork.media ?? undefined,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.artwork.id, props.artwork.media?.id, props.taskParameters],
  )

  const taskType = parsedParams?.type

  const saveableToNote = taskType === 'generator'

  const showParameters = useMemo(() => {
    if (
      parsedParams?.type === 'i2vPro' ||
      parsedParams?.type === 'ref2v' ||
      parsedParams?.type === 't2i2v'
    )
      return true
    if (parsedParams?.hidePrompts) return false

    const p = pickBy(omit(parsedParams, ['prompts']))

    return Object.keys(p).length > 0
  }, [parsedParams])

  const tabs = [[TabChoices.prompts, t('artwork:detail.tab.prompts')]] as const

  const [tab, setTab] = useState<TabChoice>(tabs[0][0])

  const isPromptsCopyable = useMemo(() => {
    return (
      (!parsedParams?.hidePrompts || isOwnArtwork) &&
      Boolean(parsedParams?.prompts)
    )
  }, [isOwnArtwork, parsedParams?.hidePrompts, parsedParams?.prompts])

  const mentionMediaMap = useMemo<MentionMediaMap | undefined>(() => {
    if (parsedParams?.type !== 'ref2v') return undefined
    return {
      imageMediaIds: parsedParams.referenceImageMediaIds,
      videoMediaIds: parsedParams.referenceVideoMediaIds,
      audioMediaIds: parsedParams.referenceAudioMediaIds,
    }
  }, [parsedParams])

  const promptsContent = (
    <ArtworkPromptsWithHidden
      prompts={parsedParams?.prompts}
      segmentedPrompts={parsedParams?.segmentedPrompts}
      shouldHide={parsedParams?.hidePrompts && !isOwnArtwork}
      mentionMediaMap={mentionMediaMap}
    />
  )

  return (
    <div>
      <div className="relative">
        <Tabs
          selectionFollowsFocus
          className="border-b flex"
          value={tab}
          onChange={(e, v) => {
            setTab(v)
          }}
          classes={{
            root: 'min-h-0',
          }}
        >
          {tabs.map(([key, label]) => {
            return (
              <Tab
                className="normal-case"
                classes={{
                  root: 'font-medium py-1 min-h-[2rem] rounded-t-lg',
                }}
                key={key}
                label={label}
                value={key}
                onClick={() => {
                  track('image_prompts_click', { value: key })
                }}
              />
            )
          })}
        </Tabs>

        <div className={cx('flex', 'absolute top-0 right-0')}>
          {saveableToNote && (
            <>
              <Button
                className="capitalize"
                onClick={() => {
                  track('save_image_as_note_click')
                  if (me) {
                    setOpenSaveDialog(true)
                  } else {
                    openSignModal(undefined, { fromPath: 'notes-save' })
                  }
                }}
              >
                <div className="flex flex-row items-center gap-1 text-sm">
                  <IcOutlineSave className="text-sm" />
                  {t('action:save')}
                </div>
              </Button>
              <UpsertNoteDialog
                open={openSaveDialog}
                onClose={() => setOpenSaveDialog(false)}
                openFrom="image"
                prefilledData={openSaveDialog ? prefilledNoteData : undefined}
              />
            </>
          )}

          <Button
            className={cx(
              'capitalize',
              // Hide copy button if prompts are not copyable
              isPromptsCopyable && tab === TabChoices.prompts
                ? 'block'
                : 'hidden',
            )}
            onClick={() => {
              if (tab === TabChoices.prompts) {
                copy(parsedParams?.prompts ?? '', { enableTrackValue: true })
              } else {
                copy(
                  (parsedParams &&
                    'negativePrompts' in parsedParams &&
                    (parsedParams.negativePrompts as string | undefined)) ||
                    '',
                  { enableTrackValue: true },
                )
              }

              if (!me) {
                openSignModal()
              }
            }}
          >
            <div className="flex flex-row items-center gap-1 text-sm">
              {copied ? (
                <FluentCheckmark16Filled className="text-sm text-green-500" />
              ) : (
                <MaterialSymbolsContentCopyOutline className="text-sm" />
              )}
              {copied ? t('action:copied') : t('common:copy')}
            </div>
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {promptsContent}
        {showParameters && <ArtworkParameter parsedParams={parsedParams} />}
        {/* Always display capabilities */}
        <ArtworkTaskCapabilityItems parsedParams={parsedParams} />
        {parsedParams?.type === 'i2vPro' ? (
          <I2vTaskFrames
            mediaId={parsedParams.mediaId}
            tailMediaId={parsedParams.tailMediaId}
            className="text-sm font-semibold"
          />
        ) : null}
      </div>
    </div>
  )
}

const ArtworkParameterItem: FC<{ title: ReactNode; value?: ReactNode }> = ({
  title,
  value,
}) => {
  if (value) {
    return (
      <>
        <dt className="font-semibold text-sm leading-6">{title}</dt>
        <dd className="text-sm text-primary/60 break-words leading-6 max-h-[9rem] overflow-auto">
          {value}
        </dd>
      </>
    )
  }
  return null
}

function ArtworkParameter(props: {
  parsedParams: ParsedParamsForArtworkDetail
}) {
  const { t } = useTranslation(['common', 'artwork', 'generation'])
  const { parsedParams } = props
  const type = parsedParams?.type

  const modelId = type === 'generator' ? parsedParams?.modelId : undefined
  const modelVersion = useModelVersion(modelId)
  const modelType = normalizeModelType(modelVersion?.model?.type)
  const isMMDIT26A = isMMDIT26AModel(modelVersion?.model?.type)

  const inferenceProfileName =
    type === 'generator' ? parsedParams?.inferenceProfile : undefined
  const { profiles } = useInferenceProfileData(modelVersion?.id)
  const inferenceProfileTitle = profiles?.find(
    p => p.profileName === inferenceProfileName,
  )?.title

  const [detailsOpen, handleDetailOpenChange] = useUncontrolled({
    defaultValue: type === 'i2vPro' || type === 'ref2v' || type === 't2i2v',
  })

  if (!parsedParams?.type) return null

  const generatorTaskParams = (() => {
    if (type !== 'generator') return []
    const styleMixSeed =
      parsedParams.styleMix?.enabled && Number(parsedParams.styleMix.seed)
        ? parsedParams.styleMix.seed
        : undefined
    const qualityTag = parsedParams.qualityTag
      ? formatQualityTagForDisplay(
          parsedParams.qualityTag,
          t(
            'generation:booster.quality-tag.add-dialog.prompt-placeholder.label',
          ),
        )
      : undefined
    const magicPrompts = parsedParams.magicPrompts
    const sizeStr =
      parsedParams.width && parsedParams.height
        ? `${parsedParams.width} x ${parsedParams.height}`
        : t('common:unknown')

    return [
      {
        key: 'magic-prompts',
        label: t('generation:original-prompts.label'),
        value: magicPrompts,
      },
      {
        key: 'inference-profile',
        label: t('generation:inference-profile.label'),
        value: inferenceProfileTitle ?? inferenceProfileName,
      },
      {
        key: 'style-key',
        label: t('generation:style.label'),
        value:
          parsedParams.modelStyle?.type === 'key'
            ? t([
                `generation:model-style.${modelType}.${parsedParams.modelStyle.keyId}`,
                parsedParams.modelStyle.keyId,
              ])
            : parsedParams.modelStyle?.type === 'custom'
              ? parsedParams.modelStyle.custom
              : undefined,
      },
      {
        key: 'style-key-ref',
        label: t('generation:style.ref-id.label'),
        value:
          parsedParams.modelStyle?.type === 'styleKeyRef'
            ? parsedParams.modelStyle.refId
            : undefined,
      },
      { key: 'style-mix', label: <StyleSeedLabel />, value: styleMixSeed },
      {
        key: 'quality-tag',
        label: t('generation:quality-tag.label'),
        value: qualityTag,
      },
      { key: 'size', label: t('generation:size.label'), value: sizeStr },
      {
        key: 'sampling-steps',
        label: t('generation:sampling-steps.label'),
        value: isMMDIT26A ? undefined : parsedParams.samplingSteps,
      },
      {
        key: 'sampling-methods',
        label: t('generation:sampling-method.label'),
        value: isMMDIT26A ? undefined : parsedParams.samplingMethod,
      },
      {
        key: 'cfgscale',
        label: t('generation:cfg-scale.label'),
        value: parsedParams.cfgScale,
      },
      {
        key: 'seed',
        label: t('generation:seed.label'),
        value: parsedParams.seed,
      },
      {
        key: 'negative',
        label: t('generation:negative-prompts.label'),
        value: parsedParams.negativePrompts,
      },
      {
        key: 'watermark',
        label: t('generation:watermark.label'),
        value: parsedParams.watermark
          ? t('generation:watermark.enabled')
          : undefined,
      },
    ]
  })()

  const i2vProTaskParams = (() => {
    if (type !== 'i2vPro' && type !== 'ref2v') return []
    // Convert model name for i18n key compatibility
    const p = parsedParams as Extract<
      typeof parsedParams,
      { type: 'i2vPro' | 'ref2v' }
    >
    const modelName = t([
      `generation:image-to-video.parameters.model.${getI18nKeyForI2vModelName(p.model)}.label`,
      'common:unknown',
    ])
    return [
      {
        key: 'model',
        label: t('generation:image-to-video.parameters.model.label'),
        value: modelName,
      },
      {
        key: 'mode',
        label: t('generation:image-to-video.parameters.mode.label'),
        value: p.mode,
      },
      {
        key: 'duration',
        label: t('generation:image-to-video.parameters.duration.label'),
        value: p.duration,
      },
      ...(type === 'i2vPro'
        ? [
            {
              key: 'camera',
              label: t('generation:image-to-video.parameters.camera.label'),
              value: (
                parsedParams as Extract<typeof parsedParams, { type: 'i2vPro' }>
              ).cameraMovement,
            },
          ]
        : []),
    ]
  })()

  return (
    <details
      open={detailsOpen}
      onToggle={e => {
        const toggleState = (e.nativeEvent as ToggleEvent).newState
        const open = toggleState === 'open'
        handleDetailOpenChange(open)
        const stateStr = open ? 'unfold' : 'fold'
        track('show_parameters_click', {
          type: stateStr,
        })
      }}
    >
      <summary className="bg-neutral-100 dark:bg-stone-700 rounded-full px-2 py-1 w-fit text-sm select-none cursor-pointer">
        {t('artwork:parameter.show-parameters')}
      </summary>
      <dl className="grid grid-cols-[fit-content(200px),minmax(0,1fr)] gap-x-4 ml-6 mt-1">
        {generatorTaskParams.map(it => (
          <ArtworkParameterItem
            key={it.key}
            title={it.label}
            value={it.value}
          />
        ))}
        {/* i2vPro task parameters */}
        {i2vProTaskParams.map(it => (
          <ArtworkParameterItem
            key={it.key}
            title={it.label}
            value={it.value}
          />
        ))}
      </dl>
    </details>
  )
}

function ArtworkParametersForWorkflow(props: {
  parsedParams: Extract<ParsedParamsForArtworkDetail, { type: 'workflow' }>
}) {
  const { beingAdmin } = useMe()

  const { workflowId, workflowName } = props.parsedParams
  const isInpaintWorkflow = Object.keys(INPAINT_MODELS).includes(workflowName)

  // Skip fetching inpaint workflows since they're private
  const { data: workflowArtwork, loading } =
    useGetWorkflowForMarketByWorkflowId(
      isInpaintWorkflow ? skipToken : { workflowId },
    )
  const hidden =
    (!loading && !workflowArtwork) ||
    workflowArtwork?.visibility !== ArtworkVisibilityType.Public

  const [{ liked, count, optimistic }, toggleLiked] = useLike(
    workflowArtwork?.id,
    RefType.Artwork,
    workflowArtwork,
  )

  /*
   * Render
   */

  if (!workflowId) return null

  return (
    <>
      <h3 className="font-medium mt-3">
        <Text i18nKey="artwork:workflow.workflow-used" />
      </h3>
      <div className="grid grid-cols-[auto_1fr_auto] items-center gap-2 p-1 -m-1 has-[button:hover]:!bg-transparent rounded-xl text-sm">
        <div className="col-span-2 grid grid-cols-subgrid items-center no-underline">
          <MediaPreview
            mediaId={workflowArtwork?.mediaId}
            className={cx(
              'row-start-1 col-start-1 flex-none h-16 aspect-[1200/630] rounded-lg',
              loading && 'animate-pulse',
            )}
          />
          {hidden && !beingAdmin && (
            <FluentEyeHide20Filled className="row-start-1 col-start-1 place-self-center relative size-5" />
          )}
          <div>
            <p>
              {loading ? (
                <TextSkeleton className="w-20" />
              ) : beingAdmin ? (
                <span className="flex items-center gap-1">
                  {hidden && (
                    <FluentEyeHide20Filled className="size-5 text-amber-600 dark:text-amber-500" />
                  )}
                  {workflowArtwork?.title}
                </span>
              ) : (
                <Text i18nKey="artwork:workflow.messages.workflow-is-hidden" />
              )}
            </p>
            <p
              className={cx(
                'flex items-center gap-1',
                hidden && !beingAdmin && 'hidden',
              )}
            >
              <UserAvatar user={workflowArtwork?.author} className="size-6" />
              {getUserDisplayName(workflowArtwork?.author)}
            </p>
          </div>
        </div>
        <div className="flex flex-col">
          <button
            className={cx(
              'inline-flex items-center gap-1 p-1 -m-1 ml-0 pr-1.5 hover:bg-black/5 dark:hover:bg-white/5 rounded-md',
              optimistic && 'opacity-50',
              loading && 'invisible',
              hidden && !beingAdmin && 'hidden',
            )}
            onClick={ev => {
              ev.stopPropagation()
              toggleLiked()
            }}
          >
            <LikeHeart liked={liked ?? false} className="size-5" />
            <span>{count}</span>
          </button>
          {/* Height of username tag */}
          <div className="h-6" />
        </div>
      </div>
      <ArtworkTaskCapabilityItems parsedParams={props.parsedParams} />
    </>
  )
}

function ArtworkParametersForArtFilter(props: {
  parsedParams: Extract<ParsedParamsForArtworkDetail, { type: 'art-filter' }>
}) {
  const { filters } = useImageArtFilters()
  const { filterId, strength } = props.parsedParams

  const filterName = filters?.find(f => f.id === filterId)?.name ?? filterId

  return (
    <div className="mt-3">
      <h3 className="font-medium">
        <Text i18nKey="generation:enhance-image.filters.pixai-filter-applied" />
      </h3>
      <dl className="grid grid-cols-[fit-content(200px),minmax(0,1fr)] gap-x-4 my-2">
        <dt className="font-semibold text-sm leading-6">
          <Text i18nKey="generation:enhance-image.filters.filter-name-label" />
        </dt>
        <dd className="text-sm text-primary/60 leading-6">{filterName}</dd>
        <dt className="font-semibold text-sm leading-6">
          <Text i18nKey="generation:enhance-image.filters.strength-label" />
        </dt>
        <dd className="text-sm text-primary/60 leading-6">
          {Math.round((strength ?? 1) * 100)}%
        </dd>
      </dl>
      <ArtworkTaskCapabilityItems parsedParams={props.parsedParams} />
    </div>
  )
}

function TextSkeleton(props: StyleProps) {
  return (
    <span
      {...props}
      className={cx('inline-flex items-center prepend-zws', props.className)}
    >
      <span className="flex-1 h-[1em] bg-zinc-300 rounded animate-pulse" />
    </span>
  )
}
